<script setup>
import { Link } from '@inertiajs/vue3'

import FacebookIcon from '@/components/icons/FacebookIcon.vue'
import InstagramIcon from '@/components/icons/InstagramIcon.vue'
import ThreadsIcon from '@/components/icons/ThreadsIcon.vue'
import { cookies, faq, home, login, privacy, register, story, terms } from '@/routes'
import galleriesRoutes from '@/routes/galleries'
import locationsRoutes from '@/routes/locations'

const year = new Date().getFullYear()

function openCookiePreferences() {
    if (typeof window !== 'undefined' && typeof window.showHideToggleCookiePreferencesModal === 'function') {
        window.showHideToggleCookiePreferencesModal()
    }
}
</script>

<template>
    <footer class="border-t border-zinc-900 bg-zinc-950 px-6 pt-16 pb-8">
        <div class="mx-auto grid max-w-7xl gap-12 md:grid-cols-[2fr_1fr_1fr_1fr]">
            <!-- Brand column - wordmark + tagline + social row stacked.
                 Social moved into this column so the link columns stay
                 visually balanced (3 equal-width text cols on the right). -->
            <div class="flex flex-col items-center md:items-start">
                <Link
                    class="teko-font text-3xl font-bold text-zinc-100 uppercase italic"
                    :href="home.url()"
                >
                    RideSnap
                </Link>
                <p class="font-body mt-3 max-w-xs text-center text-sm text-zinc-400 md:text-left">
                    Galerie motorsport fotek z českých silnic a tratí. Najdi snímky ze svých jízd a stáhni si je.
                </p>
                <div class="mt-6 flex gap-3">
                    <a
                        class="hover:text-primary flex size-9 items-center justify-center rounded bg-zinc-900 text-zinc-400 transition-colors"
                        href="https://www.facebook.com/profile.php?id=61570749668620"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                    >
                        <FacebookIcon class="size-4" />
                    </a>
                    <a
                        class="hover:text-primary flex size-9 items-center justify-center rounded bg-zinc-900 text-zinc-400 transition-colors"
                        href="https://www.instagram.com/ridesnap.cz/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                    >
                        <InstagramIcon class="size-5" />
                    </a>
                    <a
                        class="hover:text-primary flex size-9 items-center justify-center rounded bg-zinc-900 text-zinc-400 transition-colors"
                        href="https://www.threads.com/@ridesnap.cz"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Threads"
                    >
                        <ThreadsIcon class="size-4" />
                    </a>
                </div>
            </div>

            <!-- Procházet - public browse actions -->
            <div class="flex flex-col items-center gap-3 md:items-start">
                <h3 class="text-primary-container font-mono text-[11px] font-bold tracking-[0.2em] uppercase">
                    Procházet
                </h3>
                <Link
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    :href="galleriesRoutes.index.url()"
                >
                    Galerie
                </Link>
                <Link
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    :href="locationsRoutes.index.url()"
                >
                    Mapa fotospotů
                </Link>
                <Link
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    :href="locationsRoutes.suggest.url()"
                >
                    Navrhnout místo
                </Link>
            </div>

            <!-- Pro fotografy - photographer onboarding + Studio entry -->
            <div class="flex flex-col items-center gap-3 md:items-start">
                <h3 class="text-primary-container font-mono text-[11px] font-bold tracking-[0.2em] uppercase">
                    Pro fotografy
                </h3>
                <Link
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    :href="register.url({ query: { role: 'photographer' } })"
                >
                    Staň se fotografem
                </Link>
                <Link
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    :href="login.url()"
                >
                    Přihlášení do Studia
                </Link>
            </div>

            <!-- O RideSnapu - brand + support pages. Kontakt is a mailto until
                 a dedicated /kontakt page exists. -->
            <div class="flex flex-col items-center gap-3 md:items-start">
                <h3 class="text-primary-container font-mono text-[11px] font-bold tracking-[0.2em] uppercase">
                    O RideSnapu
                </h3>
                <Link
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    :href="story.url()"
                >
                    Náš příběh
                </Link>
                <Link
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    :href="faq.url()"
                >
                    FAQ
                </Link>
                <a
                    class="font-body hover:text-primary-container/80 text-sm text-zinc-400 transition-colors"
                    href="mailto:podpora@ridesnap.cz"
                >
                    Kontakt
                </a>
            </div>
        </div>

        <!-- Bottom bar - copyright + legal links + made-in badge -->
        <div
            class="mx-auto mt-12 flex max-w-7xl flex-col items-center justify-between gap-4 border-t border-zinc-900 pt-6 md:flex-row"
        >
            <p class="font-body text-xs font-medium tracking-widest text-zinc-400 uppercase">
                &copy; {{ year }} RideSnap &middot; Vyrobila
                <a
                    class="hover:text-primary-container/80 text-zinc-400 transition-colors"
                    href="https://braincode.cz"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Braincode s.r.o.
                </a>
            </p>

            <!-- Legal links - ToS + Privacy + Cookies, required for launch -->
            <nav
                class="font-body flex items-center gap-3 text-xs font-medium tracking-widest text-zinc-400 uppercase"
                aria-label="Právní dokumenty"
            >
                <Link
                    class="hover:text-primary-container/80 transition-colors"
                    :href="terms.url()"
                >
                    Pravidla
                </Link>
                <span
                    class="text-zinc-700"
                    aria-hidden="true"
                >
                    ·
                </span>
                <Link
                    class="hover:text-primary-container/80 transition-colors"
                    :href="privacy.url()"
                >
                    Soukromí
                </Link>
                <span
                    class="text-zinc-700"
                    aria-hidden="true"
                >
                    ·
                </span>
                <Link
                    class="hover:text-primary-container/80 transition-colors"
                    :href="cookies.url()"
                >
                    Cookies
                </Link>
                <span
                    class="text-zinc-700"
                    aria-hidden="true"
                >
                    ·
                </span>
                <button
                    class="font-body hover:text-primary-container/80 p-0 text-xs font-medium tracking-widest text-zinc-400 uppercase transition-colors"
                    type="button"
                    @click="openCookiePreferences"
                >
                    Nastavení cookies
                </button>
            </nav>

            <p class="font-body text-xs font-medium tracking-widest text-zinc-400 uppercase">Made in Česko 🇨🇿</p>
        </div>
    </footer>
</template>
